import type {
  ApplicationInsightsServiceOptions,
  GetPageNameForAnalytics,
  GetUserForAnalytics,
} from '@sortera/ee-analytics-web'
import { ApplicationInsightsService, ConsoleAnalyticsService } from '@sortera/ee-analytics-web'
import { ensureError } from '@sortera/ee-utils'
import type { App } from 'vue'

import { areAnalyticsEnabled, setAnalyticsService, trackError, trackPageView } from '@/services/analytics.ts'
import type { RouteLocation, Router } from '@/services/routing/index.ts'

// Ignored dependency domains
const GOOGLE_BASE_DOMAIN = 'googleapis.com' as const

// Global properties
const GLOBAL_PROP_VALUE_ROLE_NAME = 'FacilityView' as const

export function usePluginAnalytics(app: App, router: Router, getUser: GetUserForAnalytics): void {
  if (areAnalyticsEnabled()) {
    const service = getApplicationInsightsService(router, getUser)
    setAnalyticsService(service)
  } else {
    setAnalyticsService(new ConsoleAnalyticsService())
  }

  const prevErrorHandler = app.config.errorHandler
  app.config.errorHandler = (err, instance, info) => {
    const error = ensureError(err)
    trackError(error)
    // ensure to not override any other error handler:
    prevErrorHandler?.(error, instance, info)
    throw error
  }

  router.beforeEach(to => {
    trackPageView(getRouteName(to), to.fullPath)
  })

  router.onError(err => {
    trackError(err)
  })
}

function getRouteName(route: RouteLocation): string {
  return route.name?.toString() ?? route.fullPath
}

function getApplicationInsightsService(router: Router, getUser: GetUserForAnalytics): ApplicationInsightsService {
  // eslint-disable-next-line func-style
  const getPageName: GetPageNameForAnalytics = () => getRouteName(router.currentRoute.value)

  const options: ApplicationInsightsServiceOptions = {
    appInsightsConfig: {
      connectionString: import.meta.env.VITE_APPLICATIONINSIGHTS_CONNECTION_STRING,
      enableCorsCorrelation: true,
      enableRequestHeaderTracking: true,
      enableResponseHeaderTracking: true,
      enableUnhandledPromiseRejectionTracking: true,
    },
    applicationVersion: import.meta.env.VITE_APP_BUILD_ID,
    areAnalyticsEnabled,
    getPageName,
    getUser,
    ignoredDependencyDomains: [GOOGLE_BASE_DOMAIN],
    onValidationError(err, trackError) {
      trackError(err)
    },
    roleName: GLOBAL_PROP_VALUE_ROLE_NAME,
    telemetryItemInitializer: [],
  }
  return new ApplicationInsightsService(options)
}
